import React, { useState, useEffect } from "react";
import {
    Card,
    CardBody,
    CardTitle,
    Row,
    Col,
    Button,
    Container,
    Table,
    Badge,
    Input,
    Label,
    FormGroup,
    Spinner
} from "reactstrap";
import { MessageSquare, RefreshCw, UserPlus, Search, Calendar, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { getWhatsAppReplies } from "../../components/Api/GmsWhatsAppWebhook";
import { getCountries } from "../../components/Api/GmsCountry";
import {
    hasPagePermissionWithAdmin,
    hasViewOnlyPermission,
    hasEditOrHigherPermission,
    isUserAdmin
} from "../../components/Permission/PermissionService";

const GmsWhatsAppLeads = () => {
    const navigate = useNavigate();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(new Date().toLocaleTimeString());
    const [countries, setCountries] = useState([]);

    // Filters
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [fromNumber, setFromNumber] = useState("");

    // Permissions
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
    const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsWhatsAppLeads');
    const isViewOnly = hasViewOnlyPermission(permissions, '/GmsWhatsAppLeads');
    const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsWhatsAppLeads');

    useEffect(() => {
        if (!hasPermission) {
            // Redirect or handle lack of permission
            return;
        }
        loadLeads();
        loadCountries();
    }, [hasPermission]);

    const loadLeads = async () => {
        try {
            setLoading(true);
            const params = {
                onlyLeads: true,
                fromDate: fromDate || null,
                toDate: toDate || null,
                fromNumber: fromNumber || null
            };
            const data = await getWhatsAppReplies(params);
            setLeads(data);
            setLastRefresh(new Date().toLocaleTimeString());
        } catch (error) {
            console.error("Failed to load leads", error);
        } finally {
            setLoading(false);
        }
    };

    const loadCountries = async () => {
        try {
            const data = await getCountries();
            setCountries(data || []);
        } catch (error) {
            console.error("Error loading countries:", error);
        }
    };

    const handleAddCustomer = (lead) => {
        // Use lead.countryId if provided by backend, otherwise try to find it
        let countryId = lead.countryId || lead.CountryId || null;
        
        if (!countryId && (lead.CountryCode || lead.countryCode)) {
            const code = lead.CountryCode || lead.countryCode;
            const country = countries.find(c =>
                c.countryCode === code ||
                c.phoneCode === code ||
                (typeof code === 'string' && code.startsWith('+') && c.phoneCode === code.substring(1))
            );
            if (country) {
                countryId = country.country_Id;
            }
        }

        navigate('/GmsCustomer', {
            state: {
                phone: lead.fromNumber || lead.FromNumber,
                countryCode: lead.countryCode || lead.CountryCode,
                countryId: countryId
            }
        });
    };

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        loadLeads();
    };

    const handleReset = () => {
        setFromDate("");
        setToDate("");
        setFromNumber("");
        // Reload leads with null params
        setLoading(true);
        getWhatsAppReplies({ onlyLeads: true }).then(data => {
            setLeads(data);
            setLastRefresh(new Date().toLocaleTimeString());
        }).finally(() => setLoading(false));
    };

    if (!hasPermission) {
        return (
            <div className="page-content">
                <Container fluid>
                    <div className="text-center mt-5">
                        <h4 className="text-muted">Access Denied</h4>
                        <p>You do not have permission to view the WhatsApp Leads page.</p>
                    </div>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumb title="Marketing & CRM" breadcrumbItem="WhatsApp Leads" />

                <Card className="shadow-sm border-0 mb-4 bg-light bg-opacity-50">
                    <CardBody>
                        <Row className="align-items-end g-3">
                            <Col lg={3} md={6}>
                                <div className="form-group mb-0">
                                    <Label className="small fw-semibold text-muted mb-1">From Date</Label>
                                    <div className="input-group input-group-sm">
                                        <span className="input-group-text bg-white border-end-0 text-muted"><Search size={14} /></span>
                                        <Input
                                            type="date"
                                            value={fromDate}
                                            onChange={(e) => setFromDate(e.target.value)}
                                            className="form-control border-start-0 ps-0"
                                        />
                                    </div>
                                </div>
                            </Col>
                            <Col lg={3} md={6}>
                                <div className="form-group mb-0">
                                    <Label className="small fw-semibold text-muted mb-1">To Date</Label>
                                    <div className="input-group input-group-sm">
                                        <span className="input-group-text bg-white border-end-0 text-muted"><Calendar size={14} /></span>
                                        <Input
                                            type="date"
                                            value={toDate}
                                            onChange={(e) => setToDate(e.target.value)}
                                            className="form-control border-start-0 ps-0"
                                        />
                                    </div>
                                </div>
                            </Col>
                            <Col lg={3} md={6}>
                                <div className="form-group mb-0">
                                    <Label className="small fw-semibold text-muted mb-1">Lead Phone Number</Label>
                                    <div className="input-group input-group-sm">
                                        <span className="input-group-text bg-white border-end-0 text-muted"><Phone size={14} /></span>
                                        <Input
                                            type="text"
                                            placeholder="Search number..."
                                            value={fromNumber}
                                            onChange={(e) => setFromNumber(e.target.value)}
                                            className="form-control border-start-0 ps-0"
                                        />
                                    </div>
                                </div>
                            </Col>
                            <Col lg={3} md={6} className="d-flex gap-2">
                                <Button color="primary" size="sm" className="px-4 shadow-sm" onClick={() => handleSearch()}>
                                    Search
                                </Button>
                                <Button color="white" size="sm" className="border shadow-sm" onClick={handleReset}>
                                    Reset
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                <Card className="shadow-sm border-0">
                    <CardBody className="p-0">
                        <div className="p-4 border-bottom border-light d-flex justify-content-between align-items-center bg-white rounded-top">
                            <div>
                                <h5 className="mb-0 fw-bold text-primary">Potential Leads Log</h5>
                                <span className="text-muted small">Showing messages from contacts that aren't registered customers yet</span>
                            </div>
                            <div className="d-flex align-items-center gap-3">
                                <span className="text-muted small">Updated: {lastRefresh}</span>
                                <Button
                                    color="light"
                                    size="sm"
                                    className="border shadow-sm rounded-circle p-2 d-flex align-items-center justify-content-center"
                                    onClick={loadLeads}
                                    style={{ width: '32px', height: '32px' }}
                                    disabled={loading}
                                >
                                    <RefreshCw size={14} className={loading ? 'spin' : ''} />
                                </Button>
                            </div>
                        </div>

                        <div className="table-responsive">
                            <Table className="table-centered table-nowrap mb-0 table-hover">
                                <thead className="bg-light bg-opacity-50">
                                    <tr>
                                        <th className="ps-4 fw-semibold border-0 py-3" style={{ width: "180px" }}>Received At</th>
                                        <th className="fw-semibold border-0 py-3" style={{ width: "160px" }}>Phone Number</th>
                                        <th className="fw-semibold border-0 py-3" style={{ width: "160px" }}>Country</th>
                                        <th className="fw-semibold border-0 py-3">Message Body</th>
                                        <th className="text-center pe-4 fw-semibold border-0 py-3" style={{ width: "160px" }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="5" className="text-center py-5">
                                                <Spinner color="primary" size="sm" className="me-2" />
                                                <span className="text-muted">Loading leads...</span>
                                            </td>
                                        </tr>
                                    ) : leads.length > 0 ? (
                                        leads.map((msg, idx) => (
                                            <tr key={idx}>
                                                <td className="ps-4 text-muted small">
                                                    {new Date(msg.receivedAt || msg.ReceivedAt).toLocaleDateString([], { month: 'short', day: 'numeric' })} at {' '}
                                                    {new Date(msg.receivedAt || msg.ReceivedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </td>
                                                <td>
                                                    <span className="fw-semibold text-dark">
                                                        {msg.fromNumber || msg.FromNumber}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-column">
                                                        <span className="text-dark fw-medium small">
                                                            {msg.countryName || msg.CountryName || "Unknown"}
                                                        </span>
                                                        <span className="text-muted" style={{ fontSize: '10px' }}>
                                                            Prefix: {msg.countryCode || msg.CountryCode || "-"}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="text-wrap">
                                                    <span className="text-muted" style={{ display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                        {msg.messageBody || msg.MessageBody}
                                                    </span>
                                                </td>
                                                <td className="text-center pe-4">
                                                    {!isViewOnly && (
                                                        <Button
                                                            color="success"
                                                            size="sm"
                                                            className="btn-rounded btn-soft-success px-3"
                                                            onClick={() => handleAddCustomer(msg)}
                                                            style={{ border: 'none' }}
                                                        >
                                                            Add Customer
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center py-5">
                                                <div className="my-3">
                                                    <MessageSquare size={48} className="text-light mb-3" />
                                                    <h6 className="text-muted">No New WhatsApp Leads</h6>
                                                    <p className="small text-muted mb-0">All recent messages belong to registered customers.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </CardBody>
                </Card>
            </Container>

            <style>{`
                .spin { animation: rotation 2s infinite linear; }
                @keyframes rotation { from { transform: rotate(0deg); } to { transform: rotate(359deg); } }
                .btn-soft-success { 
                    background-color: rgba(52, 195, 143, 0.1); 
                    color: #34c38f; 
                    font-weight: 500;
                    font-size: 11px;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                }
                .btn-soft-success:hover { 
                    background-color: #34c38f; 
                    color: #fff; 
                }
                .table-centered td { 
                    vertical-align: middle; 
                    border-bottom: 1px solid #f2f2f7;
                    padding-top: 15px;
                    padding-bottom: 15px;
                }
                .input-group-text {
                    border-radius: 6px 0 0 6px;
                }
                .form-control {
                    border-radius: 0 6px 6px 0;
                }
                .form-control:focus {
                    box-shadow: none;
                    border-color: #ced4da;
                }
            `}</style>
        </div>
    );
};

export default GmsWhatsAppLeads;
